<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import BuhotextCampaignsList from 'dashboard/components-next/Campaigns/Pages/CampaignPage/BuhotextCampaign/BuhotextCampaignsList.vue';
import BuhotextCampaignEditor from 'dashboard/components-next/Campaigns/Pages/CampaignPage/BuhotextCampaign/BuhotextCampaignEditor.vue';
import BuhotextDevicesView from 'dashboard/components-next/Campaigns/Pages/CampaignPage/BuhotextCampaign/BuhotextDevicesView.vue';

const { t } = useI18n();
const store = useStore();

const selectedCampaignId = ref(null);
const isCreatingNew = ref(false);
const currentView = ref('campaigns'); // 'campaigns', 'devices'

const campaigns = useMapGetter('buhotextCampaigns/getCampaigns');

const shouldShowCreateButton = computed(() => {
  return currentView.value === 'campaigns';
});

const selectedCampaign = computed(() => {
  if (!selectedCampaignId.value) return null;
  return campaigns.value.find(c => c.id === selectedCampaignId.value);
});

const showEditor = computed(
  () => isCreatingNew.value || selectedCampaignId.value
);

const handleCreateCampaign = () => {
  currentView.value = 'campaigns';
  selectedCampaignId.value = null;
  isCreatingNew.value = true;
};

const handleBackToCampaigns = () => {
  currentView.value = 'campaigns';
  selectedCampaignId.value = null;
  isCreatingNew.value = false;
};

const handleSelectCampaign = campaign => {
  selectedCampaignId.value = campaign.id;
  isCreatingNew.value = false;
};

const handleCancelEdit = () => {
  selectedCampaignId.value = null;
  isCreatingNew.value = false;
};

const handleSaveCampaign = async campaignData => {
  try {
    if (isCreatingNew.value) {
      await store.dispatch('buhotextCampaigns/create', campaignData);
      useAlert(t('CAMPAIGN.BUHOTEXT.CREATE.SUCCESS_MESSAGE'));
      // Recargar la lista de campañas desde 66text
      await store.dispatch('buhotextCampaigns/get');
    } else {
      await store.dispatch('buhotextCampaigns/update', {
        id: selectedCampaignId.value,
        ...campaignData,
      });
      useAlert(t('CAMPAIGN.BUHOTEXT.UPDATE.SUCCESS_MESSAGE'));
      // Recargar la lista de campañas desde 66text
      await store.dispatch('buhotextCampaigns/get');
    }
    handleCancelEdit();
  } catch (error) {
    const errorMessage =
      error?.response?.data?.error ||
      error?.message ||
      t('CAMPAIGN.BUHOTEXT.SAVE.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

const handleDeleteCampaign = async campaignId => {
  try {
    await store.dispatch('buhotextCampaigns/delete', campaignId);
    useAlert(t('CAMPAIGN.BUHOTEXT.DELETE.SUCCESS_MESSAGE'));
    // Recargar la lista de campañas desde 66text
    await store.dispatch('buhotextCampaigns/get');
    if (selectedCampaignId.value === campaignId) {
      handleCancelEdit();
    }
  } catch (error) {
    const errorMessage =
      error?.response?.data?.error ||
      t('CAMPAIGN.BUHOTEXT.DELETE.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

const handleSendCampaign = async campaignId => {
  try {
    await store.dispatch('buhotextCampaigns/send', campaignId);
    useAlert(t('CAMPAIGN.BUHOTEXT.SEND.SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage =
      error?.response?.data?.error || t('CAMPAIGN.BUHOTEXT.SEND.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

onMounted(() => {
  store.dispatch('buhotextCampaigns/get');
});
</script>

<template>
  <CampaignLayout
    :header-title="t('CAMPAIGN.BUHOTEXT.HEADER_TITLE')"
    :button-label="
      shouldShowCreateButton ? t('CAMPAIGN.BUHOTEXT.NEW_CAMPAIGN') : ''
    "
    @click="handleCreateCampaign"
  >
    <!-- Vista de Devices -->
    <BuhotextDevicesView
      v-if="currentView === 'devices'"
      @back="handleBackToCampaigns"
    />

    <!-- Vista de lista de campañas -->
    <div v-else-if="currentView === 'campaigns'" class="space-y-4">
      <div class="flex gap-6 h-full">
        <div class="w-96 flex-shrink-0">
          <BuhotextCampaignsList
            :campaigns="campaigns"
            :selected-id="selectedCampaignId"
            @select="handleSelectCampaign"
            @delete="handleDeleteCampaign"
            @send="handleSendCampaign"
          />
        </div>

        <div class="flex-1 min-w-0">
          <BuhotextCampaignEditor
            v-if="showEditor"
            :campaign="selectedCampaign"
            :is-creating="isCreatingNew"
            @save="handleSaveCampaign"
            @cancel="handleCancelEdit"
          />
          <div
            v-else
            class="flex items-center justify-center h-full text-n-slate-11"
          >
            <div class="text-center">
              <p class="text-lg font-medium mb-2">
                {{ t('CAMPAIGN.BUHOTEXT.SELECT_OR_CREATE') }}
              </p>
              <p class="text-sm">
                {{ t('CAMPAIGN.BUHOTEXT.SELECT_OR_CREATE_HINT') }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </CampaignLayout>
</template>
